/* eslint-disable react/jsx-pascal-case */
import { isPlatform } from '@ionic/react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import {
  ArrowDownTrayIcon,
  MinusCircleIcon,
  PencilIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid'
import { Button, IconButton, Tooltip } from '@mui/material'
import {
  MaterialReactTable,
  MRT_EditActionButtons,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table'
import produce from 'immer'
import { CSVLink } from 'react-csv'

import { client } from '../../services/client'
import { useToast } from '../../contexts/toastContext'
import { columns } from './columns'

import type { MaterialReactTableProps, MRT_Row } from 'material-react-table'
import type { Associate } from './stylists-setup.schema'

type Props = {
  data: Array<Associate>
  disabled: boolean
  isLoading: boolean
}

function useAssignAllClients() {
  const queryClient = useQueryClient()
  const setToast = useToast()

  return useMutation(
    async () => {
      client
        .post('functions/updateAssociateAssignClients', {
          json: { assignAll: true },
          timeout: 90000,
          retry: {
            limit: 0,
          },
        })
        .json()
    },
    {
      onError: (error) => {
        const errorMsg =
          error instanceof Error ? error.message : 'Unknown error.'

        setToast({
          message: `Something went wrong while updating: ${errorMsg}`,
          color: 'danger',
        })
      },
      onSettled: () =>
        queryClient.invalidateQueries([{ entity: 'stylists', scope: 'setup' }]),
    }
  )
}

function useUpdateStylistClients() {
  const queryClient = useQueryClient()
  const setToast = useToast()

  return useMutation(
    async ({
      objectId,
      groupId,
      groupPosId,
    }: {
      objectId: string
      groupId: string
      groupPosId?: string
    }) =>
      client
        .post('functions/updateAssociateAssignClients', {
          json: { objectId, groupId, groupPosId, assignAll: false },
        })
        .json(),
    {
      onError: (error) => {
        const errorMsg =
          error instanceof Error ? error.message : 'Unknown error.'

        setToast({
          message: `Something went wrong while updating: ${errorMsg}`,
          color: 'danger',
        })
      },
      onSettled: () =>
        queryClient.invalidateQueries([{ entity: 'stylists', scope: 'setup' }]),
    }
  )
}

function useClearStylistClients() {
  const queryClient = useQueryClient()
  const setToast = useToast()

  return useMutation(
    async (id: string) =>
      client.post('functions/updateAssociateClearClients', {
        json: { objectId: id },
      }),
    {
      onError: (error) => {
        const errorMsg =
          error instanceof Error ? error.message : 'Unknown error.'

        setToast({
          message: `Something went wrong while updating: ${errorMsg}`,
          color: 'danger',
        })
      },
      onSettled: () =>
        queryClient.invalidateQueries([{ entity: 'stylists', scope: 'setup' }]),
    }
  )
}

function useUpdateAssociate() {
  const queryClient = useQueryClient()
  const setToast = useToast()

  return useMutation(
    async (associate) =>
      client
        .post('functions/updateAssociate', {
          json: associate,
        })
        .json(),
    {
      onMutate: async (
        variables: Pick<
          Associate,
          'objectId' | 'groupMemberId' | 'goal' | 'workDays' | 'groupPosId'
        >
      ) => {
        await queryClient.cancelQueries([
          { entity: 'stylists', scope: 'setup' },
        ])

        // Snapshot the previous value
        const previousStylists = queryClient.getQueryData<Array<Associate>>([
          { entity: 'stylists', scope: 'setup' },
        ])

        if (previousStylists) {
          const updatedStylists = produce(previousStylists, (draft) => {
            if (!draft) return previousStylists

            // Optimistically update to the new value
            const n = draft.map((d) => {
              if (d.objectId === variables.objectId) {
                d.goal = variables.goal
                d.workDays = variables.workDays
                d.groupPosId = variables.groupPosId
              }
              return d
            })
          })

          queryClient.setQueryData<Array<Associate>>(
            [{ entity: 'stylists', scope: 'setup' }],
            updatedStylists
          )
        }
        return { previousStylists }
      },
      onError: (error, _variables, context) => {
        if (context?.previousStylists) {
          queryClient.setQueryData<Array<Associate>>(
            [{ entity: 'stylists', scope: 'setup' }],
            context.previousStylists
          )
        }

        const errorMsg =
          error instanceof Error ? error.message : 'Unknown error.'

        setToast({
          message: `Something went wrong while updating: ${errorMsg}`,
          color: 'danger',
        })
      },
      onSettled: () =>
        queryClient.invalidateQueries([{ entity: 'stylists', scope: 'setup' }]),
    }
  )
}

function StylistsSetupTable({ data, disabled, isLoading }: Props) {
  const assignClients = useUpdateStylistClients()
  const assignAllClients = useAssignAllClients()
  const clearStylistClients = useClearStylistClients()
  const updateAssociate = useUpdateAssociate()

  function handleAssignAll() {
    assignAllClients.mutate()
  }

  function handleAutoAssignClients(row: MRT_Row<Associate>) {
    assignClients.mutate({
      objectId: row.id,
      groupId: row.original.groupId,
      groupPosId: row.original.groupPosId,
    })
  }

  function handleClearClients(id: string) {
    clearStylistClients.mutate(id)
  }

  const handleSaveRow: MaterialReactTableProps<Associate>['onEditingRowSave'] =
    async ({ exitEditingMode, row, values }) => {
      const { groupMemberId } = row.original

      updateAssociate.mutate({ ...values, objectId: row.id, groupMemberId })
      exitEditingMode()
    }

  const isMutating =
    assignClients.isLoading ||
    assignAllClients.isLoading ||
    clearStylistClients.isLoading ||
    updateAssociate.isLoading

  return (
    <MaterialReactTable
      columns={columns}
      data={data}
      initialState={{ showGlobalFilter: true, density: 'comfortable' }}
      state={{ isLoading, showAlertBanner: false }}
      positionToolbarAlertBanner="none"
      defaultColumn={{ size: 0 }}
      enableColumnActions={false}
      enableColumnFilters={false}
      enableColumnOrdering
      enableDensityToggle={false}
      enableEditing // not all tables have
      editingMode="row" // not all tables have
      enableFullScreenToggle={false}
      enableHiding={false} // not all tables have
      enableRowActions // not all tables have
      enableSorting={false}
      getRowId={(originalRow) => originalRow.objectId} // not all tables have (useful with edit)
      onEditingRowSave={handleSaveRow} // not all tables have
      renderToolbarInternalActions={({ table }) => (
        <>
          <MRT_ToggleGlobalFilterButton table={table} />
          <Button
            disabled={disabled || isMutating}
            color="primary"
            variant="contained"
            onClick={handleAssignAll}
          >
            Assign All
          </Button>
          {!isPlatform('capacitor') ? (
            <Tooltip title="Export">
              <span className="flex">
                <CSVLink
                  filename="users.csv"
                  className="text-ion-color-step-400 disabled:text-ion-color-step-700 flex items-center justify-center p-2"
                  data={table
                    .getRowModel()
                    .rows.map(({ original: { photo, objectId, ...rest } }) => ({
                      ...rest,
                    }))}
                  headers={table
                    .getFlatHeaders()
                    //  filter out photo and row action headers
                    .filter((header) => header.id !== 'photo')
                    .filter((header) => header.id !== 'mrt-row-actions')
                    .map((header) => ({
                      key: header.column.id,
                      label: (header.column.columnDef.header ?? '') as string,
                    }))}
                >
                  <ArrowDownTrayIcon className=" size-6" />
                </CSVLink>
              </span>
            </Tooltip>
          ) : null}
        </>
      )}
      renderRowActions={({ row, table }) => (
        <div className="nowrap flex gap-2">
          {table.getState().editingRow?.id === row.id ? (
            <MRT_EditActionButtons table={table} row={row} />
          ) : (
            <Tooltip title="Edit">
              <span className="flex">
                <IconButton
                  className="text-slate-500"
                  onClick={() => {
                    table.setEditingRow(row)
                  }}
                  disabled={disabled || isMutating}
                >
                  <PencilIcon className="size-5" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {table.getState().editingRow?.id === row.id ? null : (
            <>
              <Tooltip title="Auto-Assign Clients">
                <span className="flex">
                  <IconButton
                    color="primary"
                    onClick={() => handleAutoAssignClients(row)}
                    disabled={disabled || isMutating}
                  >
                    <PlusCircleIcon className="size-6" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Un-Assign Clients">
                <span className="flex">
                  <IconButton
                    color="error"
                    onClick={() => {
                      handleClearClients(row.id)
                    }}
                    disabled={disabled || isMutating}
                  >
                    <MinusCircleIcon className="size-6" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </div>
      )}
    />
  )
}

export { StylistsSetupTable }
